import { useMemo } from 'react';
import { T, FONT } from '../deck/theme';
import { ActOverview } from '../deck/acts/ActOverview';
import { ActDetails } from '../deck/acts/ActDetails';
import { ActMethod } from '../deck/acts/ActMethod';
import { ActResults } from '../deck/acts/ActResults';
import { ActNextSteps } from '../deck/acts/ActNextSteps';
import { ActClose } from '../deck/acts/ActClose';
import { ActDisclosures } from '../deck/acts/ActDisclosures';
import { OptimizationDeepDive } from '../deck/plan/OptimizationDeepDive';
import {
  buildCloseVM,
  buildDetailsVM,
  buildDisclosuresVM,
  buildNextStepsVM,
  buildOptimizations,
  buildOverviewVM,
  buildResultsVM,
} from '../formatters/deckContent';
import type { AdvisorMeta, ReportEnrichment, WebReportPayload } from '../types/webReport';

// The PDF renderer prints Letter with 0.5in margins → 720px printable width.
// Charts live inside Stage (40px side padding) and usually a Card (24px padding),
// so the widest safe fixed chart is 720 − 80 − 48 = 592. 580 leaves slack.
const CHART_WIDTH = 580;

interface Props {
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  advisor: AdvisorMeta | null;
}

const noop = () => {};

function Section({
  label,
  children,
  last,
  newPage,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
  newPage?: boolean;
}) {
  return (
    <div
      style={{
        ...(last
          ? { paddingBottom: 40 }
          : { borderBottom: `1px solid ${T.grayLine}`, paddingBottom: 40, marginBottom: 40 }),
        // Every act after Overview opens on a fresh PDF page.
        ...(newPage ? { breakBefore: 'page' as const, pageBreakBefore: 'always' as const } : {}),
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: T.gray,
          padding: '0 40px',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * Print/PDF composition of the deck — the seven acts stacked single-column,
 * every optimization deep dive expanded and shown optimized, fixed-width
 * charts with animations off, no chrome/analytics/interactivity. Rendered by
 * the headless PDF renderer via /:slug/print.
 */
export function PrintReportView({ payload, enrichment, advisor }: Props) {
  const optimizations = useMemo(() => buildOptimizations(payload, enrichment), [payload, enrichment]);
  const overviewVM = useMemo(() => buildOverviewVM(payload, enrichment, advisor), [payload, enrichment, advisor]);
  const detailsVM = useMemo(() => buildDetailsVM(payload, enrichment), [payload, enrichment]);
  const resultsVM = useMemo(() => buildResultsVM(enrichment, optimizations), [enrichment, optimizations]);
  const nextSteps = useMemo(() => buildNextStepsVM(optimizations, enrichment), [optimizations, enrichment]);
  const closeVM = useMemo(
    () => buildCloseVM(payload, enrichment, advisor, optimizations),
    [payload, enrichment, advisor, optimizations]
  );
  const disclosuresVM = useMemo(() => buildDisclosuresVM(payload, enrichment, advisor), [payload, enrichment, advisor]);

  const brandLabel = advisor?.company || advisor?.name || 'PlanScout';

  return (
    <div style={{ fontFamily: FONT, background: '#fff', color: T.ink, minHeight: '100vh' }}>
      {/* PDF pagination rules: keep cards and chart blocks whole across page
          breaks, and keep section labels attached to what follows them. Only
          this print page injects these — the interactive deck is untouched. */}
      <style>{`
        .deck-card, .deck-chart-block {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        /* A card that CONTAINS chart blocks may split BETWEEN them (the blocks
           themselves stay whole) — otherwise a page-tall panel card gets bumped
           to a fresh page and strands its section headline on a blank page. */
        .deck-card:has(.deck-chart-block) {
          break-inside: auto;
          page-break-inside: auto;
        }
        /* "Your starting plan" (inside the Details act) opens its own page. */
        .print-page-break {
          break-before: page;
          page-break-before: always;
        }
      `}</style>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 0 24px' }}>
        <Section label="Overview">
          <ActOverview vm={overviewVM} />
        </Section>

        <Section label="Details" newPage>
          <ActDetails
            vm={detailsVM}
            payload={payload}
            enrichment={enrichment}
            mode="view"
            setMode={noop}
            slug=""
            interactive={false}
            chartWidth={CHART_WIDTH}
          />
        </Section>

        <Section label="Method" newPage>
          <ActMethod />
        </Section>

        <Section label="Plan" newPage>
          {optimizations.map((o, i) => (
            <OptimizationDeepDive
              key={o.key}
              vm={o}
              index={i}
              count={optimizations.length}
              onBack={noop}
              onSwitch={noop}
              payload={payload}
              enrichment={enrichment}
              interactive={false}
              forceOptimized
              chartWidth={CHART_WIDTH}
            />
          ))}
        </Section>

        <Section label="Results" newPage>
          <ActResults vm={resultsVM} />
        </Section>

        <Section label="Next Steps" newPage>
          <ActNextSteps steps={nextSteps} />
        </Section>

        <Section label="Close" newPage>
          <ActClose vm={closeVM} onRestart={noop} interactive={false} />
        </Section>

        <Section label="Disclosures" last newPage>
          <ActDisclosures vm={disclosuresVM} />
        </Section>

        <div
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: T.gray,
            borderTop: `1px solid ${T.grayLine}`,
            paddingTop: 18,
            margin: '0 40px',
          }}
        >
          {brandLabel} · Generated from your interactive plan
        </div>
      </div>
    </div>
  );
}
